import { reactive } from 'vue';
import Big from 'big.js';
import { INVOICE_VAT_RATE } from '@/constants/invoiceDefaults';

// Configure Big.js for financial calculations
Big.DP = 10;
Big.RM = Big.roundHalfUp;

/**
 * Converts a value to a Big.js number, handling various input types.
 * @param {*} value - The value to convert.
 * @param {Array} [errors] - Optional errors array to push parsing errors to.
 * @param {string} [context] - Context for error reporting.
 * @returns {Big} The Big.js number.
 */
export const toBig = (value, errors = null, context = '') => {
	if (value instanceof Big) return value;
	if (value === null || value === undefined || value === '') return new Big(0);
	try {
		const raw = String(value).trim();
		const hasComma = raw.includes(',');
		const hasDot = raw.includes('.');
		const normalized =
			hasComma && !hasDot
				? raw.replace(/[£\s]/g, '').replace(',', '.')
				: raw.replace(/[£\s,]/g, '');
		const cleaned = normalized.trim();
		return new Big(cleaned || 0);
	} catch (e) {
		if (errors && Array.isArray(errors)) {
			errors.push({ context, value, error: e.message });
		}
		return new Big(0);
	}
};

/**
 * Formats a number as currency with 2 decimal places.
 * @param {*} val - The value to format.
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (val) => toBig(val).toFixed(2);

/**
 * Calculates the amount for a line item based on its amount mode.
 * @param {object} item - The line item object.
 * @returns {Big} The calculated amount as a Big.js number.
 */
export const calculateLineAmount = (item) => {
	if (!item) return new Big(0);
	const mode = (item.amountMode || '').toLowerCase();
	if (mode === 'direct') {
		return toBig(item.directAmount);
	}
	return toBig(item.hours).times(toBig(item.rate));
};

/**
 * Calculates the amount for a line item and returns it as a number.
 * @param {object} item - The line item object.
 * @returns {number} The calculated amount.
 */
export const calculateLineAmountNumber = (item) => {
	return Number(calculateLineAmount(item).toFixed(2));
};

/**
 * Calculates summary totals (subtotal, VAT, total) for invoice content.
 * @param {object} content - The invoice content with dateRanges and customDates.
 * @param {number|null} vatRateOverride - Optional VAT rate override.
 * @returns {{subtotal: number, vat: number, total: number}} The summary totals.
 */
export const calculateInvoiceSummary = (content = {}, vatRateOverride = null) => {
	let subtotal = new Big(0);
	const addLines = (lines = []) => {
		lines.forEach((line) => {
			const roundedLine = toBig(calculateLineAmount(line).toFixed(2));
			subtotal = subtotal.plus(roundedLine);
		});
	};
	(content.dateRanges || []).forEach((range) => addLines(range.items || []));
	(content.customDates || []).forEach((entry) => addLines(entry.items || []));
	const vatRate = vatRateOverride !== null ? vatRateOverride : content.vatRate ?? INVOICE_VAT_RATE;
	const roundedSubtotal = toBig(subtotal.toFixed(2));
	const roundedVat = toBig(subtotal.times(toBig(vatRate)).toFixed(2));
	const roundedTotal = roundedSubtotal.plus(roundedVat);
	return {
		subtotal: Number(roundedSubtotal.toFixed(2)),
		vat: Number(roundedVat.toFixed(2)),
		total: Number(roundedTotal.toFixed(2)),
	};
};

/**
 * Vue composable for invoice calculations with reactive error tracking.
 */
export function useInvoiceCalculations() {
	const state = reactive({
		errors: [],
	});

	const toBigWithErrors = (value, context = '') => {
		return toBig(value, state.errors, context);
	};

	const calculateAmount = (item) => {
		if (!item) return new Big(0);
		const mode = (item.amountMode || '').toLowerCase();
		if (mode === 'direct') {
			return toBigWithErrors(item.directAmount, 'directAmount');
		}
		return toBigWithErrors(item.hours, 'hours').times(toBigWithErrors(item.rate, 'rate'));
	};

	const calculateItemAmount = (item) => formatCurrency(calculateAmount(item));

	const calculateSummary = (content = {}, vatRateOverride = null) => {
		return calculateInvoiceSummary(content, vatRateOverride);
	};

	const clearErrors = () => {
		state.errors.length = 0;
	};

	const hasErrors = () => state.errors.length > 0;

	return {
		state,
		toBig: toBigWithErrors,
		formatCurrency,
		calculateAmount,
		calculateItemAmount,
		calculateSummary,
		clearErrors,
		hasErrors,
	};
}
